import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { CategoryWithChildren, CategoryImage } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLangFade } from '../hooks/useLangFade';

interface CategoryPageProps {
  hideUsefulTexts?: boolean;
}

const CategoryPage: React.FC<CategoryPageProps> = ({ hideUsefulTexts }) => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useScript();
  const langFade = useLangFade();
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState<CategoryWithChildren | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Admin: add image
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [newOrientation, setNewOrientation] = useState<'horizontal'|'vertical'>('horizontal');
  const [showAddImage, setShowAddImage] = useState(false);

  // Admin: add child category
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChildTitle, setNewChildTitle] = useState('');
  const [newChildSlug, setNewChildSlug] = useState('');
  const [newChildDesc, setNewChildDesc] = useState('');
  const [newChildCover, setNewChildCover] = useState('');
  const [savingChild, setSavingChild] = useState(false);

  useDocumentTitle(
    category ? `${category.title} — Манастир Градац` : 'О Манастиру — Манастир Градац',
    `/${slug}`,
    category?.description
  );

  const loadCategory = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    api.getCategoryBySlug(slug)
      .then(data => { setCategory(data); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || 'Категорија није пронађена.');
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => { loadCategory(); }, [loadCategory]);

  useEffect(() => {
    if (slug === 'korisni-tekstovi' && hideUsefulTexts && !isAdmin) {
      navigate('/');
    }
  }, [slug, hideUsefulTexts, isAdmin, navigate]);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null || !category) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min((i ?? 0) + 1, category.images.length - 1));
      if (e.key === 'ArrowLeft') setLightboxIndex(i => Math.max((i ?? 0) - 1, 0));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxIndex, category]);

  const handleDeleteChild = async (id: number) => {
    if (!token || !window.confirm(t('Обрисати ову категорију?'))) return;
    try {
      await api.deleteCategory(token, id);
      loadCategory();
    } catch {}
  };

  const handleAddImage = async () => {
    if (!token || !slug || !newImageUrl) return;
    try {
      await api.addCategoryImage(token, slug, { imageUrl: newImageUrl, caption: newCaption, orientation: newOrientation });
      setNewImageUrl('');
      setNewCaption('');
      setNewOrientation('horizontal');
      setShowAddImage(false);
      loadCategory();
    } catch {}
  };

  const handleToggleOrientation = async (img: CategoryImage) => {
    if (!token) return;
    try {
      const updatedOrientation = img.orientation === 'vertical' ? 'horizontal' : 'vertical';
      await api.updateCategoryImage(token, img.id, img.caption, updatedOrientation);
      loadCategory();
    } catch {}
  };

  const handleToggleCoverOrientation = async () => {
    if (!token || !category) return;
    try {
      const updatedOrientation = category.coverImageOrientation === 'vertical' ? 'horizontal' : 'vertical';
      await api.updateCategory(token, category.id, {
        title: category.title,
        slug: category.slug,
        description: category.description,
        coverImageUrl: category.coverImageUrl,
        coverImageOrientation: updatedOrientation
      });
      loadCategory();
    } catch {}
  };

  const handleDeleteImage = async (imgId: number) => {
    if (!token || !window.confirm(t('Обрисати ову слику?'))) return;
    try {
      await api.deleteCategoryImage(token, imgId);
      loadCategory();
    } catch {}
  };

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  // Cyrillic → Latin slug generator
  const generateSlug = (text: string): string => {
    const map: Record<string, string> = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'dj', 'е': 'e',
      'ж': 'z', 'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj',
      'м': 'm', 'н': 'n', 'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's',
      'т': 't', 'ћ': 'c', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'c',
      'џ': 'dz', 'ш': 's',
    };
    return text.toLowerCase()
      .split('')
      .map(ch => map[ch] || ch)
      .join('')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80);
  };

  const handleAddChild = async () => {
    if (!token || !category || !newChildTitle.trim() || !newChildSlug.trim()) return;
    setSavingChild(true);
    try {
      await api.createCategory(token, {
        parentId: category.id,
        title: newChildTitle,
        slug: newChildSlug,
        description: newChildDesc,
        coverImageUrl: newChildCover,
      });
      setNewChildTitle('');
      setNewChildSlug('');
      setNewChildDesc('');
      setNewChildCover('');
      setShowAddChild(false);
      setSavingChild(false);
      loadCategory();
    } catch {
      setSavingChild(false);
    }
  };

  // Render description with markdown-like formatting (**bold** and \n)
  const renderDescription = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
      // Simple bold: **text** -> <strong>text</strong>
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <React.Fragment key={i}>
          {parts.map((part, j) =>
            j % 2 === 1 ? <strong key={j}>{t(part)}</strong> : <span key={j}>{t(part)}</span>
          )}
          {i < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  if (loading) {
    return (
      <div className="category-page">
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="category-page">
        <div className="state-block state-error" role="alert">
          <span>{t(error || 'Категорија није пронађена.')}</span>
          <Link to="/" className="state-retry">{t('Назад')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="category-page">
      {/* Breadcrumb */}
      <nav className="category-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">{t('Почетна')}</Link>
        {category.parentId && (
          <>
            <span className="breadcrumb-sep">›</span>
            <span>{t(category.title)}</span>
          </>
        )}
        {!category.parentId && (
          <>
            <span className="breadcrumb-sep">›</span>
            <span>{t(category.title)}</span>
          </>
        )}
      </nav>

      {/* Header */}
      <header className="category-header">
        <h1 className="news-heading">{t(category.title)}</h1>
        {isAdmin && (
          <div className="news-admin-toolbar">
            <button className="btn-primary" onClick={() => navigate(`/${slug}/uredi`)}>
              {t('Уреди')} ✎
            </button>
          </div>
        )}
      </header>

      <div className={`category-content ${langFade}`}>
        {/* Description */}
        {category.description && (
          <div className="category-description">
            <p>{renderDescription(category.description)}</p>
          </div>
        )}

        {/* Cover image */}
        {category.coverImageUrl && (
          <div style={{ position: 'relative' }}>
            <div className={`category-cover ${slug === 'freskopis' ? 'frescoes-cover' : ''} ${category.coverImageOrientation === 'vertical' ? 'category-cover-vertical' : ''}`}>
              <img
                src={category.coverImageUrl}
                alt={category.title}
                onError={handleImgError}
                loading="eager"
                decoding="async"
              />
            </div>
            {isAdmin && (
              <button
                className="gallery-toggle-orientation"
                style={{
                  position: 'absolute', top: 40, right: 10,
                  background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                  borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', zIndex: 10
                }}
                onClick={handleToggleCoverOrientation}
                title={t('Промени оријентацију насловне слике')}
              >
                {category.coverImageOrientation === 'vertical' ? '⬍' : '⬌'}
              </button>
            )}
          </div>
        )}

        {/* Subcategories */}
        {category.children.length > 0 && (
          (() => {
            if (slug === 'istorija') {
              const rows = [];
              for (let i = 0; i < category.children.length; i += 2) {
                rows.push(category.children.slice(i, i + 2));
              }

              return (
                <section className="category-children timeline-layout">
                  <h2 className="category-section-title">{t('Историја и оснивање')}</h2>
                  <div className="timeline-snake">
                    {rows.map((row, rowIndex) => (
                      <div key={rowIndex} className={`timeline-row ${rowIndex % 2 !== 0 ? 'row-reverse' : ''}`}>
                        {row.map((child, colIndex) => (
                          <div key={child.id} className="timeline-snake-item">
                            <Link to={`/${child.slug}`} className="timeline-content timeline-link" style={{ position: 'relative' }}>
                              {child.coverImageUrl && (
                                <div className="timeline-img-wrapper">
                                  <img src={child.coverImageUrl} alt={child.title} onError={handleImgError} loading="lazy" />
                                </div>
                              )}
                              <div className="timeline-text">
                                <h3>{t(child.title)}</h3>
                                <p>{t(child.description).split('\n')[0].slice(0, 150)}{child.description.length > 150 ? '...' : ''}</p>
                                <span className="read-more-link">{t('Прочитај више')} &rarr;</span>
                              </div>
                              {isAdmin && (
                                <div className="news-admin-overlay news-admin-overlay-visible" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                                  <button className="news-admin-edit" onClick={(e) => { e.preventDefault(); navigate(`/${child.slug}/uredi`); }} title={t('Уреди')}>✎</button>
                                  <button className="news-admin-delete" onClick={(e) => { e.preventDefault(); handleDeleteChild(child.id); }} title={t('Обриши')}>✕</button>
                                </div>
                              )}
                            </Link>
                          </div>
                        ))}
                        {/* Connection line going down to next row */}
                        {rowIndex < rows.length - 1 && (
                          <div className={`timeline-row-connector ${rowIndex % 2 !== 0 ? 'left-connector' : 'right-connector'}`}></div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            if (slug === 'arhitektura') {
              return (
                <section className="category-children architecture-layout">
                  <h2 className="category-section-title">{t('Архитектура храма')}</h2>
                  <div className="architecture-grid">
                    {category.children.map(child => (
                      <Link key={child.id} to={`/${child.slug}`} className="architecture-card" style={{ position: 'relative' }}>
                        {child.coverImageUrl && (
                          <img src={child.coverImageUrl} alt={child.title} onError={handleImgError} loading="lazy" />
                        )}
                        <div className="architecture-card-overlay">
                          <h3>{t(child.title)}</h3>
                        </div>
                        {isAdmin && (
                          <div className="news-admin-overlay news-admin-overlay-visible" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                            <button className="news-admin-edit" onClick={(e) => { e.preventDefault(); navigate(`/${child.slug}/uredi`); }} title={t('Уреди')}>✎</button>
                            <button className="news-admin-delete" onClick={(e) => { e.preventDefault(); handleDeleteChild(child.id); }} title={t('Обриши')}>✕</button>
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                </section>
              );
            }

            if (slug === 'poseta') {
              return (
                <section className="category-children visit-layout">
                  <h2 className="category-section-title">{t('Планирајте посету')}</h2>
                  <div className="visit-grid">
                    {category.children.map(child => {
                      let icon = '📍';
                      const titleLower = child.title.toLowerCase();
                      if (titleLower.includes('vreme') || titleLower.includes('време') || titleLower.includes('weather')) icon = '⛅';
                      if (titleLower.includes('put') || titleLower.includes('пут') || titleLower.includes('kako') || titleLower.includes('како') || titleLower.includes('directions')) icon = '🗺️';
                      if (titleLower.includes('smeštaj') || titleLower.includes('смештај') || titleLower.includes('accommodation')) icon = '🏨';

                      return (
                        <Link key={child.id} to={`/${child.slug}`} className="visit-card" style={{ position: 'relative' }}>
                          <div className="visit-icon">{icon}</div>
                          <div className="visit-body">
                            <h3>{t(child.title)}</h3>
                            <p>{t(child.description).split('\n')[0].slice(0, 100)}{child.description.length > 100 ? '...' : ''}</p>
                          </div>
                          {isAdmin && (
                            <div className="news-admin-overlay news-admin-overlay-visible" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                              <button className="news-admin-edit" onClick={(e) => { e.preventDefault(); navigate(`/${child.slug}/uredi`); }} title={t('Уреди')}>✎</button>
                              <button className="news-admin-delete" onClick={(e) => { e.preventDefault(); handleDeleteChild(child.id); }} title={t('Обриши')}>✕</button>
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </section>
              );
            }

            // Default
            return (
              <section className="category-children">
                <h2 className="category-section-title">{t('Подкатегорије')}</h2>
                <div className="children-grid">
                  {category.children.map(child => (
                    <Link
                      key={child.id}
                      to={`/${child.slug}`}
                      className="child-card"
                    >
                      {child.coverImageUrl ? (
                        <img
                          src={child.coverImageUrl}
                          alt={child.title}
                          className="child-card-img"
                          onError={handleImgError}
                          loading="lazy"
                        />
                      ) : (
                        <div className="child-card-placeholder">☦</div>
                      )}
                      <div className="child-card-body">
                        <h3>{t(child.title)}</h3>
                        <p>{t(child.description).split('\n')[0].slice(0, 100)}{child.description.length > 100 ? '...' : ''}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })()
        )}

        {/* Admin: Add child category */}
        {isAdmin && category && (
          <div className="category-admin-add">
            {!showAddChild ? (
              <button className="btn-primary" onClick={() => setShowAddChild(true)}>
                {t('+ Додај категорију')}
              </button>
            ) : (
              <div className="category-add-form">
                <h3 className="category-section-title">{t('Нова подкатегорија')}</h3>

                <div className="form-group">
                  <label className="post-admin-label">{t('Наслов')}</label>
                  <input
                    type="text"
                    className="post-admin-input"
                    value={newChildTitle}
                    onChange={e => {
                      setNewChildTitle(e.target.value);
                      // Auto-generate slug as user types
                      if (!newChildSlug || newChildSlug === generateSlug(newChildTitle)) {
                        setNewChildSlug(generateSlug(e.target.value));
                      }
                    }}
                    placeholder={t('Нпр. Оснивање и питање ктиторства')}
                  />
                </div>

                <div className="form-group">
                  <label className="post-admin-label">{t('URL адреса (slug)')}</label>
                  <p className="form-hint">
                    {t('Линк:')}{' '}
                    <code style={{ color: 'var(--gold-solid)' }}>/{newChildSlug || '...'}</code>
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: 'rgba(100,80,55,0.6)', fontFamily: 'monospace' }}>/</span>
                    <input
                      type="text"
                      className="post-admin-input"
                      value={newChildSlug}
                      onChange={e => setNewChildSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      style={{ fontFamily: 'monospace', flex: 1 }}
                      placeholder="npr-osnivanje-i-pitanje"
                    />
                  </div>
                </div>

                <ImageUpload
                  label={t('Насловна слика (cover)')}
                  imageUrl={newChildCover}
                  onImageUrlChange={setNewChildCover}
                />

                <div className="form-group">
                  <label className="post-admin-label">{t('Опис / Текст')}</label>
                  <textarea
                    rows={8}
                    className="post-admin-textarea"
                    value={newChildDesc}
                    onChange={e => setNewChildDesc(e.target.value)}
                    placeholder={t('Текст странице...')}
                  />
                </div>

                <div className="post-admin-actions">
                  <button
                    className="btn-primary"
                    onClick={handleAddChild}
                    disabled={savingChild || !newChildTitle.trim() || !newChildSlug.trim()}
                  >
                    {savingChild ? t('Чување...') : t('Сачувај')}
                  </button>
                  <button className="btn-secondary" onClick={() => {
                    setShowAddChild(false);
                    setNewChildTitle('');
                    setNewChildSlug('');
                    setNewChildDesc('');
                    setNewChildCover('');
                  }}>
                    {t('Откажи')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Photo Gallery */}
        {(category.images.length > 0 || isAdmin) && (
          <section className="category-gallery">
            <h2 className="category-section-title">
              {t('Фото галерија')}
              {category.images.length > 0 && (
                <span className="gallery-count">({category.images.length})</span>
              )}
            </h2>

            {category.images.length > 0 && (
              <div className="gallery-grid">
                {category.images.map((img, index) => (
                  <div key={img.id} className={`gallery-item ${img.orientation === 'vertical' ? 'gallery-item-vertical' : ''}`}>
                    <button
                      className="gallery-item-btn"
                      onClick={() => setLightboxIndex(index)}
                      aria-label={img.caption || `${t('Слика')} ${index + 1}`}
                    >
                      <img
                        src={img.imageUrl}
                        alt={img.caption || `${category.title} ${index + 1}`}
                        onError={handleImgError}
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="gallery-item-overlay">
                        <span className="gallery-zoom-icon">🔍</span>
                      </div>
                    </button>
                    {img.caption && <p className="gallery-caption">{t(img.caption)}</p>}
                    {isAdmin && (
                      <div className="gallery-admin-controls" style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: '8px' }}>
                        <button
                          className="gallery-toggle-orientation"
                          style={{
                            background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                            borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px'
                          }}
                          onClick={() => handleToggleOrientation(img)}
                          title={t('Промени оријентацију')}
                        >
                          {img.orientation === 'vertical' ? '⬍' : '⬌'}
                        </button>
                        <button
                          className="gallery-delete"
                          style={{ position: 'static' }}
                          onClick={() => handleDeleteImage(img.id)}
                          title={t('Обриши слику')}
                        >✕</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Admin: Add Image */}
            {isAdmin && (
              <div className="gallery-admin">
                {!showAddImage ? (
                  <button className="btn-primary" onClick={() => setShowAddImage(true)}>
                    {t('+ Додај слику')}
                  </button>
                ) : (
                  <div className="gallery-add-form">
                    <ImageUpload
                      label={t('Слика')}
                      imageUrl={newImageUrl}
                      onImageUrlChange={setNewImageUrl}
                    />
                    <div>
                      <label className="post-admin-label">{t('Опис слике (опционо)')}</label>
                      <input
                        type="text"
                        className="post-admin-input"
                        value={newCaption}
                        onChange={e => setNewCaption(e.target.value)}
                        placeholder={t('Нпр. Поглед на наос из припрате')}
                      />
                    </div>
                    <div>
                      <label className="post-admin-label">{t('Оријентација')}</label>
                      <select
                        className="post-admin-input"
                        value={newOrientation}
                        onChange={e => setNewOrientation(e.target.value as 'horizontal' | 'vertical')}
                      >
                        <option value="horizontal">{t('Хоризонтална (Landscape)')}</option>
                        <option value="vertical">{t('Вертикална (Portrait)')}</option>
                      </select>
                    </div>
                    <div className="post-admin-actions">
                      <button className="btn-primary" onClick={handleAddImage} disabled={!newImageUrl}>
                        {t('Сачувај')}
                      </button>
                      <button className="btn-secondary" onClick={() => { setShowAddImage(false); setNewImageUrl(''); setNewCaption(''); setNewOrientation('horizontal'); }}>
                        {t('Откажи')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && category.images[lightboxIndex] && (
        <div
          className="lightbox"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label={t('Преглед слике')}
        >
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxIndex(null)} aria-label={t('Затвори')}>✕</button>
            {lightboxIndex > 0 && (
              <button className="lightbox-prev" onClick={() => setLightboxIndex(lightboxIndex - 1)} aria-label={t('Претходна')}>‹</button>
            )}
            <img
              src={category.images[lightboxIndex].imageUrl}
              alt={category.images[lightboxIndex].caption || category.title}
            />
            {category.images[lightboxIndex].caption && (
              <p className="lightbox-caption">{t(category.images[lightboxIndex].caption)}</p>
            )}
            <div className="lightbox-counter">{lightboxIndex + 1} / {category.images.length}</div>
            {lightboxIndex < category.images.length - 1 && (
              <button className="lightbox-next" onClick={() => setLightboxIndex(lightboxIndex + 1)} aria-label={t('Следећа')}>›</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryPage;
